import { styled } from '@mui/material/styles';
import { useState, useRef } from 'react';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { FormattedMessage } from 'react-intl';
import { fetchGameById, fetchPartners } from '@/lib/api';
import { mapFiltersToParams } from '../Resources/ResourcePageHelpers';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { GameSchema, PartnerSchema } from '@/lib/types';
import { useParams } from 'react-router';
import Breadcrumbs from './Breadcrumbs';
import GameHeader from './GameHeader';
import Tabs from './Tabs';
import PartnersTable from './PartnersTable';
import ComplianceDocumentsTable from './ComplianceDocumentsTable';
import MarketingAssetsTable from './MarketingAssetsTable';
import { useQuery } from '@tanstack/react-query';
import { useNotFoundRedirect } from '@/hooks/useNotFoundRedirect';
import { NavigationHeight } from '@/components/Navigation/NavigationHelpers';

const GameAdminPage = () => {
  const params = useParams();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedTab, setSelectedTab] = useState('partners');
  const [partnersFilters, setPartnersFilters] = useState<
    Partial<FilterSchema>
  >({});
  const [complianceDocumentsFilters, setComplianceDocumentsFilters] =
    useState<Partial<FilterSchema>>({});
  const [marketingAssetsFilters, setMarketingAssetsFilters] =
    useState<Partial<FilterSchema>>({});
  const partnersApiParams = {
    game_ids: [Number(params.gameId || 0)],
    ...mapFiltersToParams(partnersFilters),
  };

  const gameQuery = useQuery<GameSchema>({
    queryKey: ['games', params.gameId],
    queryFn: () => fetchGameById(Number(params.gameId || 0)),
    enabled: !!params.gameId,
    retry: false,
  });

  useNotFoundRedirect({
    queryResult: gameQuery,
    checkResourceExists: (data) => !!data,
  });

  const { is_bespoke_branded } = gameQuery.data || {};

  const { data: partnersData, refetch: refetchPartners } =
    useInfiniteScroll({
      fetchFn: fetchPartners,
      queryKey: ['partners', ...Object.values(partnersApiParams)],
      params: partnersApiParams,
      scrollContainerRef:
        scrollContainerRef as React.RefObject<HTMLElement>,
      pageSize: 20,
      scrollThreshold: 200,
    });

  return (
    <Root>
      <div
        ref={scrollContainerRef}
        className="page-width-wrapper wide-scrollbar"
      >
        <Breadcrumbs />
        {params.gameId && (
          <>
            <GameHeader gameId={params.gameId} />
            <StyledTabs
              value={selectedTab}
              onChange={setSelectedTab}
              options={[
                {
                  id: 'partners',
                  label: (
                    <FormattedMessage
                      id="partners"
                      defaultMessage="Partners"
                    />
                  ),
                },
                {
                  id: 'complianceDocuments',
                  label: (
                    <FormattedMessage
                      id="complianceDocuments"
                      defaultMessage="Compliance Documents"
                    />
                  ),
                },
                {
                  id: 'marketingAssets',
                  label: (
                    <FormattedMessage
                      id="marketingAssets"
                      defaultMessage="Marketing Assets"
                    />
                  ),
                },
              ]}
            />
            {selectedTab === 'partners' ? (
              <PartnersTable
                gameId={Number(params.gameId || 0)}
                isBespokeBranded={is_bespoke_branded || false}
                partners={(partnersData || []) as PartnerSchema[]}
                filters={partnersFilters}
                onFiltersChange={setPartnersFilters}
                onChangePartners={() => refetchPartners()}
              />
            ) : null}
            {selectedTab === 'complianceDocuments' ? (
              <ComplianceDocumentsTable
                gameId={Number(params.gameId || 0)}
                filters={complianceDocumentsFilters}
                onFiltersChange={setComplianceDocumentsFilters}
                scrollContainerRef={scrollContainerRef}
              />
            ) : null}
            {selectedTab === 'marketingAssets' ? (
              <MarketingAssetsTable
                gameId={Number(params.gameId || 0)}
                gameGroup={gameQuery.data?.game_group}
                filters={marketingAssetsFilters}
                onFiltersChange={setMarketingAssetsFilters}
                scrollContainerRef={scrollContainerRef}
              />
            ) : null}
          </>
        )}
      </div>
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  height: '100%',
  background: '#F9FAFB',
  '> *': {
    maxHeight: `calc(100vh - ${NavigationHeight}px)`,
    overflowY: 'auto',
    width: '100%',
  },
});

const StyledTabs = styled(Tabs)({
  margin: '0px 40px',
});

export default GameAdminPage;
